import React from 'react';
import { Search } from 'lucide-react';
import Pagination from '@mui/material/Pagination';
import { StatusBadge } from '../../ui';
import { getPropImagePath, handleImageError } from '../../../utils/imageUtils';

const ImagesView = ({
  paginatedProps,
  totalPages,
  currentPage,
  onPageChange,
  addToCart,
  setSelectedProp,
  isItemInBasket,
  onViewDetails
}) => {
  return (
    <div className="space-y-6">
      {paginatedProps.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {paginatedProps.map(prop => (
              <div key={prop.id} className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                {/* Large Image */}
                <div className="aspect-square relative overflow-hidden">
                  <img 
                    src={getPropImagePath(prop)}
                    alt={prop.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => handleImageError(e, prop.category)}
                  />
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <StatusBadge status={prop.status} />
                  </div>
                  
                  {/* Distance Badge */}
                  <div className="absolute top-2 right-2">
                    <span className="bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                      {Math.round(prop.distance)} mi
                    </span>
                  </div>
                  
                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-2">
                      {prop.status === 'available' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(prop.id);
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isItemInBasket?.(prop.id) 
                              ? 'bg-red-600 text-white hover:bg-red-700' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isItemInBasket?.(prop.id) ? 'Remove from Cart' : 'Add to Cart'}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails?.(prop.id);
                          setSelectedProp?.(prop);
                        }}
                        className="bg-white text-gray-900 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Minimal Info */}
                <div className="p-3">
                  <h3 className="font-medium text-sm truncate mb-1">{prop.name || prop.title}</h3>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{prop.owner || 'Unknown Warehouse'}</span>
                    <span>{prop.location || 'Location TBD'}</span>
                  </div>
                  {prop.tags && prop.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {prop.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                      {prop.tags.length > 2 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          +{prop.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination 
            totalPages={totalPages} 
            currentPage={currentPage} 
            onPageChange={onPageChange} 
          />
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Search className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>No props found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default ImagesView;
